<template>
  <div class="flex w-full h-full mx-auto p-6">
    <div class="bg-white shadow-lg rounded-lg w-2/6 mx-4 pt-4 pb-64 px-4 overflow-hidden">
      <div class="flex items-center pb-4">
        <h3 class="flex items-center font-bold text-gray-900 px-2 py-2 leading-tight tracking-wider">
          Lista de gestores
          <BaseBadge>{{filteredMonitorsList.length}}</BaseBadge>
        </h3> 

        <BaseFab @click="newDataModal()" variant="success" size="sm" fixedLeft >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="fill-current text-white h-6 w-6"
            viewBox="0 0 24 24"
          >
            <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </BaseFab> 
         
      </div>

      <!-- Campo: Search -->
      <BaseInputText 
        name="search"
        v-model="search"  
        placeholder="Búsqueda de gestores"
        :value="search"
      /> 

      <div class="w-full flex gap-1 pt-4 pb-2"> 
        <BaseButton
          variant="secondary"
          v-on:click="statusFilterMonitors = 'all', filteredMonitorsList"
          v-bind:class="[statusFilterMonitors === 'all' ? 'border-transparent text-white' : 'bg-transparent text-cdmx-blue-light']"
          outline
        >
          Todos
        </BaseButton>

        <BaseButton
          variant="secondary"
          v-on:click="statusFilterMonitors = 'active', filteredMonitorsList"
          v-bind:class="[statusFilterMonitors === 'active' ? 'border-transparent text-white' : 'bg-transparent text-cdmx-blue-light']"
          outline
        >
          Activos
        </BaseButton>

        <BaseButton
          variant="secondary"
          v-on:click="statusFilterMonitors = 'inactive', filteredMonitorsList"
          v-bind:class="[statusFilterMonitors === 'inactive' ? 'border-transparent text-white' : 'bg-transparent text-cdmx-blue-light']"
          outline
        >
          Inactivos
        </BaseButton> 
        <BaseButton @click="changeFormOrder" variant="secondary" outline tooltip="Ordenar registros">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 fill-current" viewBox="0 0 24 24">
            <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
            <path d="M0 0h24v24H0z" fill="none" />
          </svg>
        </BaseButton> 
      </div>

      <div class="h-full overflow-auto">
        <!-- Lista de autoridades -->
        <div v-if="filteredMonitorsList.length">
          <BaseListItem
            v-for="currentMonitors in filteredMonitorsList"
            :key="currentMonitors.id"
            :item="{
              inactive: currentMonitors.inactive,
              image: currentMonitors.profile_image,
              title: `${currentMonitors.name} ${currentMonitors.last_name}`,
              subtitle: currentMonitors.email,
              description: currentMonitors.name_category,
              ...currentMonitors
            }"
            :selected-id="monitor && monitor.id"
            @select="getMonitor"
          />
        </div>

        <div v-else class="text-gray-500 text-sm italic mt-3 px-5">
          No se encontraron resultados.
        </div>
      </div>
    </div>
    <div class="bg-white shadow-lg rounded-lg w-4/6 mx-auto p-8 flex flex-col items-center text-center overflow-auto">
      <!-- Imagen -->
      <div class="flex justify-center mb-6" v-if="monitor">
        <img
          :src="monitor.profile_image" 
          class="object-cover h-40 w-40 rounded-full border-4"
          :class="monitor.inactive ? 'border-cdmx-red' : 'border-cdmx-green'"
        />
      </div>

      <!-- Información personal -->
      <div class="w-full" v-if="monitor">
        <h2 class="font-bold text-gray-900 text-lg mb-3">Información Personal</h2>
        <div class="text-sm space-y-2">
          <div>
            <span class="font-bold text-gray-900">Nombre:</span>
            <span class="ml-2 text-gray-700">{{ monitor.name }} {{ monitor.last_name }}</span>
          </div>
          <div>
            <span class="font-bold text-gray-900">Correo:</span>
            <span class="ml-2 text-gray-700">{{ monitor.email }}</span>
          </div>
          <div v-if="monitor.address">
            <span class="font-bold text-gray-900">Dirección:</span>
            <span class="ml-2 text-gray-700">{{ monitor.address }}</span>
          </div>
          <div v-if="monitor.phone_number">
            <span class="font-bold text-gray-900">Teléfono:</span>
            <span class="ml-2 text-gray-700">{{ monitor.phone_number }}</span>
          </div>
          <div v-if="monitor.sex">
            <span class="font-bold text-gray-900">Sexo:</span>
            <span class="ml-2 text-gray-700">{{ monitor.sex }}</span>
          </div>
          <div v-if="monitor.age">
            <span class="font-bold text-gray-900">Edad:</span>
            <span class="ml-2 text-gray-700">{{ monitor.age }} años</span>
          </div>
        </div>
      </div>

      <!-- Botones -->
      <div class="flex flex-wrap justify-center gap-3 mt-8">
        <BaseButton
          @click="updateDataModal(monitor)"
          variant="info"
          icon="pen"
          outline
        >
          Editar
        </BaseButton>

        <BaseButton
          v-if="monitor.inactive"
          @click="updateActivated(false)"
          variant="success"
          icon="check-circle"
          outline
        >
          Activar
        </BaseButton>

        <BaseButton
          v-if="!monitor.inactive"
          @click="updateActivated(true)"
          variant="danger"
          icon="times-circle"
          outline
        >
          Desactivar
        </BaseButton>

        <BaseButton
          @click="showingConfirm = true"
          variant="danger"
          icon="trash-alt"
          outline
        >
          Eliminar
        </BaseButton>
      </div>

      <!-- Bitácora -->
      <div class="w-full mt-12">
        <h2 class="font-bold text-gray-900 text-lg mb-3">Bitácora</h2>
        <div class="bg-white shadow-md rounded-lg p-4">
          <div class="flex items-center justify-end mb-4 gap-2">
            <input
              type="date"
              v-model="searchByday"
              class="w-1/3 appearance-none border rounded py-2 px-3 text-gray-900 bg-gray-200 focus:outline-none focus:bg-white focus:border-cdmx-blue-light"
            />
            <BaseButton
              variant="info"
              icon="times"
              outline 
              @click="clearSelectedDate()"
            >
              Limpiar
            </BaseButton>
          </div>

          <table class="text-left w-full border-collapse">
            <thead>
              <tr>
                <th class="py-4 px-6 bg-gray-100 font-bold uppercase text-sm text-gray-900 border-b border-gray-300">Módulo</th>
                <th class="py-4 px-6 bg-gray-100 font-bold uppercase text-sm text-gray-900 border-b border-gray-300">Acción</th>
                <th class="py-4 px-6 bg-gray-100 font-bold uppercase text-sm text-gray-900 border-b border-gray-300">Observación</th>
                <th class="py-4 px-6 bg-gray-100 font-bold uppercase text-sm text-gray-900 border-b border-gray-300">
                  Fecha
                  <button
                    class="border-2 rounded border-gray-700 inline-flex items-center justify-center p-1 ml-2"
                    @click="changeFormOrderBinnacles"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 fill-current" viewBox="0 0 24 24">
                      <path d="M16 17.01V10h-2v7.01h-3L15 21l4-3.99h-3zM9 3L5 6.99h3V14h2V6.99h3L9 3z" />
                      <path d="M0 0h24v24H0z" fill="none" />
                    </svg>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <template v-for="type in filteredBinnacles" >
                <tr class="hover:bg-gray-100" :key="type.id">
                  <td class="py-4 px-6 border-b border-gray-300">{{ type.module }}</td>
                  <td class="py-4 px-6 border-b border-gray-300">{{ type.action }}</td>
                  <td
                    v-html="type.observation"
                    class="py-4 px-6 border-b border-gray-300 whitespace-pre-line"
                  ></td>
                  <td class="py-4 px-6 border-b border-gray-300">
                    {{ type.date | moment('DD/MM/YYYY hh:mm a') }}
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </div>
    </div> 

    <monitor-modal
      :newMonitor="newMonitor"
      :showing="showMonitorModal"
      :viewModal="viewModal"
      @updateMonitor="updateMonitor"
      @updateAllMonitors="updateAllMonitors"
      @close="showMonitorModal = false"
    ></monitor-modal>

    <alert-dialog :showing="showingConfirm">
      <div class="flex items-center justify-center">
        <div class="uppercase text-gray-900">¿Desea eliminar este gestor?</div>
      </div>
      <div class="w-full flex items-center justify-center">
        <button
          class="mt-4 px-4 py-2 rounded-lg text-gray-900 border-red-500 border-4 font-bold tracking-wider bg-white hover:bg-red-300"
          @click="updateActivity()"
        >Eliminar</button>
        <button
          class="mt-4 px-4 py-2 ml-2 rounded-lg text-gray-900 border-gray-500 border-4 font-bold tracking-wider bg-white hover:bg-gray-300"
          @click="showingConfirm = false"
        >Cerrar</button>
      </div>
    </alert-dialog>

    <alert-dialog :showing="showingSuccessDelete">
      <div class="flex items-center justify-center">
        <div class="uppercase text-gray-900">Eliminado exitosamente</div>
        <div class="ml-2">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="w-8 lg:w-12 h-8 lg:h-12 fill-current text-cdmx-green"
            viewBox="0 0 24 24"
          >
            <path fill="none" d="M0 0h24v24H0z" />
            <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z" />
          </svg>
        </div>
      </div>
      <div class="w-full">
        <button
          class="mt-4 px-4 py-2 rounded-lg text-gray-900 border-gray-500 border-4 font-bold tracking-wider bg-white hover:bg-gray-300"
          @click="showingSuccessDelete = false"
        >Cerrar</button>
      </div>
    </alert-dialog>
  </div>
</template>


<script>
import { backend_url } from "../config/url.config.ts";
import MonitorModal from "./MonitorModal";
import AlertDialog from "./AlertDialog";
import EventBus from "../event-bus";
import axios from "axios";
import XLSX from 'xlsx'
import moment from "moment";
import BaseButton from "./atoms/BaseButton.vue";
import BaseListItem from "./atoms/BaseListItem.vue";
import BaseBadge from "./atoms/BaseBadge.vue";
import BaseFab from "./atoms/BaseFab.vue";
import BaseInputText from "./atoms/BaseInputText.vue";
import defaultProfileImg from "@/assets/default-profile.png";

export default {
  name: "MonitorsList",
  data() {
    return {
      searchByday: "",
      binnacles: [],
      json_fields: {
        Nombre: "name",
        Apellido: "last_name",
        Correo: "email",
        Edad: "age",
        Sexo: "sex",
        Telefono: "phone_number",
        Dirección: "address",
        "Url de perfil": "profile_image",
      },
      json_meta: [
        [
          {
            key: "charset",
            value: "utf-8"
          }
        ]
      ],
      showingConfirm: false,
      showingSuccessDelete: false,
      showMonitorModal: false,
      selectedIndex: 0,
      MonitorsList: [],
      monitor: null,
      newMonitor: false,
      viewModal: "monitor_list",
      search: "",
      statusFilterMonitors: "all",
      config: {
        headers: {
          "x-access-token": localStorage.getItem("accessToken")
        }
      },
      formOrder: "upward",
      formOrderBinnacle: "upward"
    };
  },
  components: {
    MonitorModal,
    AlertDialog,
    BaseButton,
    BaseListItem,
    BaseBadge,
    BaseFab,
    BaseInputText
  },
  mounted() {
    this.getMonitorsList();
  },
  methods: {
    startDownload(){
      this.axios.post(backend_url+"/api/binnacles/monitor/save/",{
          "module": "Monitoristas", 
          "action": "Descarga de información de todos los monitoristas", 
          "observation": 'Información personal de las autoridades',
          "email": localStorage.getItem("email")
        },
        this.config
      )
      .then(response => {
        //console.log(response)
      })
      .catch(error => {
        //console.log(error)
      });
    },
    onexport () {
      const info = {
        "Nombre" : this.monitor.name,
        "Apellido": this.monitor.last_name,
        "Correo": this.monitor.email,
        "Telefono": this.monitor.phone_number,
        "Edad": this.monitor.age,
        "Sexo": this.monitor.sex
      }

      let binnacles = [];
      this.binnacles.forEach(binnacle => {
        const newBinnacle = {
          Modulo: binnacle.module,
          Acción: binnacle.action,
          Observacion: binnacle.observation,
          Fecha: moment(binnacle.date).format("DD/MM/YYYY hh:mm A"),
        };
        binnacles.push(newBinnacle);
      });

      const monitorInfo = XLSX.utils.json_to_sheet([info]) 
      const binnaclesList = XLSX.utils.json_to_sheet(binnacles);
      var wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(wb, monitorInfo, 'Monitorista')
      XLSX.utils.book_append_sheet(wb, binnaclesList, 'Bitacora')

      XLSX.writeFile(wb, 'Monitorista.xlsx')

      this.axios.post(backend_url+"/api/binnacles/monitor/save/",{
          "module": "Monitoristas", 
          "action": "Descarga de información de un monitorista", 
          "observation": `${this.monitor.name} ${this.monitor.last_name}`,
          "email": localStorage.getItem("email")
        },
        this.config
      )
      .then(response => {
        //console.log(response)
      })
      .catch(error => {
        //console.log(error)
      });
    },
    getMonitorsList: function() {
      axios
        .get(backend_url + "/monitors", this.config)
        .then(response => {
          this.MonitorsList = response.data;
          this.MonitorsList.forEach(data => {
            if (data.profile_image && data.profile_image !== "null" && data.profile_image !== "") {
              data.profile_image = backend_url + "/" + data.profile_image;
            } else {
              data.profile_image = defaultProfileImg;
            }
          });

          if (this.MonitorsList.length > 0) {
            this.monitor = !this.monitor ? this.MonitorsList[0] : this.monitor;
            this.fetchAllBinnacles(this.monitor.email)
          }
        })
        .catch(error => {
          console.log(error);
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
          this.errored = true;
        })
        .finally(() => (this.loading = false));
    },
    getMonitor: function(monitor) {
      this.monitor = monitor;
      this.fetchAllBinnacles(this.monitor.email)
    },
    fetchAllBinnacles(email) {
      this.axios
        .post(
          backend_url + "/api/binnacles/monitor/list",
          {
            "email": email
          },
          this.config
        )
        .then(response => {
          this.binnacles = response.data.binnacles;
        })
        .catch(error => {
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
        });
    },

    updateDataModal: function(monitor) {
      this.showMonitorModal = true;
      this.newMonitor = false;
      EventBus.$emit("editModal", monitor);
    },

    newDataModal: function() {
      this.showMonitorModal = true;
      this.newMonitor = true;
    },

    updateMonitor: function(Monitor) {
      this.MonitorsList.forEach(monitor => {
        if (monitor.id == Monitor.id) {
          let updateMonitor = Object.assign({}, Monitor);
          monitor.name = updateMonitor.name;
          monitor.last_name = updateMonitor.last_name;
          monitor.address = updateMonitor.address;
          monitor.phone_number = updateMonitor.phone_number;
          monitor.age = updateMonitor.age;
          monitor.sex = updateMonitor.sex;
          monitor.is_admin = updateMonitor.is_admin;
          monitor.profile_image = updateMonitor.profile_image;
        }
      });
    },
    updateAllMonitors: function() {
      this.getMonitorsList();
    },

    updateActivity: function() {
      this.axios
        .delete(backend_url + "/monitors/" + this.monitor.id, this.config)
        .then(response => {
          return this.axios.post(backend_url+"/api/binnacles/monitor/save/",{
              "module": "Monitoristas", 
              "action": "Eliminación de un monitorista", 
              "observation": `${this.monitor.name} ${this.monitor.last_name}`,
              "email": localStorage.getItem("email")
            },
            this.config
          )
        })
        .then(response => {
          this.showingConfirm = false;
          this.showingSuccessDelete = true;
          this.getMonitorsList();
        })
        .catch(error => {
          console.error(error.response);
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
        });
    },
    updateActivated: function(status) {
      this.axios
        .post(
          backend_url + "/users/updateActivity",
          {
            inactive: status,
            id: this.monitor.user_id
          },
          this.config
        )
        .then(response => {
          this.monitor.inactive = status; 
          let action = !status ? "Activación de un gestor" : "Desactivación de un gestor";
          return this.axios.post(backend_url+"/api/binnacles/monitor/save/",{
            "module": "Gestores", 
            "action": action, 
            "observation": `${this.monitor.name} ${this.monitor.last_name}`,
            "email": localStorage.getItem("email")
            },
            this.config
          ) 
        })
        .then (() => {
          this.getMonitorsList();
        })
        .catch(error => {
          console.error(error.response);
          if (error.response.status == 400 || error.response.status == 401) {
            if (
              error.response.data.name === "TokenExpiredError" ||
              error.response.data.name === "InvalidTokenError" ||
              error.response.data.name === "JsonWebTokenError"
            ) {
              this.$router.push({ path: "/login" });
            }
          }
        });
    },    
    loadDefaultImg(event) {
      event.target.src = defaultProfileImg;
    },
    changeFormOrder() {
      if (this.formOrder === "upward") {
        this.formOrder = "downward";
      } else {
        this.formOrder = "upward";
      }
    },
    changeFormOrderBinnacles() {
      if (this.formOrderBinnacle === "upward") {
        this.formOrderBinnacle = "downward";
      } else {
        this.formOrderBinnacle = "upward";
      }
    },
    clearSelectedDate() {
      this.searchByday = "";
    }
  },
  computed: {
    filteredMonitorsList: function() {
      return this.MonitorsList.filter(monitor => {
        let full_name = monitor.name + " " + monitor.last_name;
        switch (this.statusFilterMonitors) {
          case "all":
            return (
              full_name.toLowerCase().includes(this.search.toLowerCase()) ||
              monitor.phone_number
                .toLowerCase()
                .includes(this.search.toLowerCase()) ||
              monitor.email.toLowerCase().includes(this.search.toLowerCase())
            );
          case "active":
            return (
              (full_name.toLowerCase().includes(this.search.toLowerCase()) ||
                monitor.phone_number
                  .toLowerCase()
                  .includes(this.search.toLowerCase()) ||
                monitor.email
                  .toLowerCase()
                  .includes(this.search.toLowerCase())) &&
              !monitor.inactive
            );
          case "inactive":
            return (
              (full_name.toLowerCase().includes(this.search.toLowerCase()) ||
                monitor.phone_number
                  .toLowerCase()
                  .includes(this.search.toLowerCase()) ||
                monitor.email
                  .toLowerCase()
                  .includes(this.search.toLowerCase())) &&
              monitor.inactive
            );
          default:
            break;
        }
      }).sort((a, b) => {
        if (this.formOrder === "upward") {
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        } else {
          if (a.name.toLowerCase() > b.name.toLowerCase()) {
            return -1;
          }
          if (a.name.toLowerCase() < b.name.toLowerCase()) {
            return 1;
          }
          return 0;
        }
      });
    },
    filteredBinnacles() {
      return this.binnacles.filter(binnacle => {
        let date = moment(binnacle.date).format("YYYY-MM-DD");
        return date.toLowerCase().includes(this.searchByday.toLowerCase());
      }).sort((a, b) => {
        if (this.formOrderBinnacle === "upward") {
          return new Date(a.date) - new Date(b.date);
        } else {
          return new Date(b.date) - new Date(a.date);
        }
      });
    }
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.bg-semi-75 {
  background-color: rgba(0, 0, 0, 0.75);
}
</style>
